(ns app.front.view.group.reporting.bonds
  (:require [app.domain.bond.model :as bond-model]
            [app.front.entities :as entities]
            [app.front.ui.select :as ui-select]
            [app.front.util :refer [for-frag]]
            [app.front.view.group.admin.subs :as gas]
            [app.front.view.group.reporting.common :as reporting-common]
            [app.front.view.group.reporting.download-button :as rdb]
            [app.front.view.group.reporting.filters :as reporting-filters]
            [app.front.view.group.reporting.list :as report-list]
            [core.front.carbon :as cc]
            [core.front.date-util :as date-util]
            [core.front.event :refer [<sub >evt]]
            [core.util.util :as util]
            [re-frame.core-instrumented :as rf]
            [schema.core :as s]))

(def expiration-date-range-options
  [:reporting.bonds.expiration-options/next-30-days
   :reporting.bonds.expiration-options/next-90-days
   :reporting.bonds.expiration-options/next-6-months
   :reporting.bonds.expiration-options/next-year
   :reporting.bonds.expiration-options/custom])

(def closing-date-range-options
  [:reporting.bonds.closing-options/last-30-days
   :reporting.bonds.closing-options/last-90-days
   :reporting.bonds.closing-options/last-6-months
   :reporting.bonds.closing-options/last-year
   :reporting.bonds.closing-options/custom])

(rf/reg-sub ::bond-templates
  :-> ::bond-templates)

(rf/reg-sub ::bond-type-options
  :<- [::bond-templates]
  (fn [bond-templates [_ lang]]
    (mapv (juxt :db/id #(bond-model/template-name % lang))
          bond-templates)))

(def bond-states
  [:bond.state/unverified
   :bond.state/verified
   :bond.state/expired
   :bond.state/closed
   :bond.state/pending-invite
   :bond.state/cancelled-invite])

;; Expiration date range state management
(rf/reg-sub ::expiration-date-selector-mode
  :-> ::expiration-date-selector-mode)

(rf/reg-sub ::expiration-custom-start-date
  :-> ::expiration-custom-start-date)

(rf/reg-sub ::expiration-custom-end-date
  :-> ::expiration-custom-end-date)

(rf/reg-sub ::expiration-date-range
  :<- [::expiration-date-selector-mode]
  :<- [::expiration-custom-start-date]
  :<- [::expiration-custom-end-date]
  (fn [[mode custom-start custom-end] _]
    ;; Return nil if no mode selected (no filtering)
    (when mode
      (let [now (js/Date.)]
        (case mode
          :reporting.bonds.expiration-options/next-30-days
          [now (date-util/add-days-to-date now 30)]
          :reporting.bonds.expiration-options/next-90-days
          [now (date-util/add-days-to-date now 90)]
          :reporting.bonds.expiration-options/next-6-months
          [now (date-util/add-days-to-date now 180)]
          :reporting.bonds.expiration-options/next-year
          [now (date-util/add-days-to-date now 365)]
          :reporting.bonds.expiration-options/custom
          [custom-start custom-end]
          nil)))))

(rf/reg-event-db ::set-expiration-date-selector-mode
  (fn [db [_ mode]]
    (assoc db ::expiration-date-selector-mode mode)))

(rf/reg-event-db ::select-expiration-date
  (fn [db [_ id date]]
    (assoc db id date)))

;; Closing date range state management
(rf/reg-sub ::closing-date-selector-mode
  :-> ::closing-date-selector-mode)

(rf/reg-sub ::closing-custom-start-date
  :-> ::closing-custom-start-date)

(rf/reg-sub ::closing-custom-end-date
  :-> ::closing-custom-end-date)

(rf/reg-sub ::closing-date-range
  :<- [::closing-date-selector-mode]
  :<- [::closing-custom-start-date]
  :<- [::closing-custom-end-date]
  (fn [[mode custom-start custom-end] _]
    ;; Return nil if no mode selected (no filtering)
    (when mode
      (let [now (js/Date.)]
        (case mode
          :reporting.bonds.closing-options/last-30-days
          [(date-util/add-days-to-date now -30) now]
          :reporting.bonds.closing-options/last-90-days
          [(date-util/add-days-to-date now -90) now]
          :reporting.bonds.closing-options/last-6-months
          [(date-util/add-days-to-date now -180) now]
          :reporting.bonds.closing-options/last-year
          [(date-util/add-days-to-date now -365) now]
          :reporting.bonds.closing-options/custom
          [custom-start custom-end]
          nil)))))

(rf/reg-event-db ::set-closing-date-selector-mode
  (fn [db [_ mode]]
    (assoc db ::closing-date-selector-mode mode)))

(rf/reg-event-db ::select-closing-date
  (fn [db [_ id date]]
    (assoc db id date)))

(rf/reg-event-db ::set-selected-types
  (fn [db [_ v]]
    (assoc db ::selected-types v)))

(rf/reg-sub ::selected-types
  #(-> % ::selected-types set))

(rf/reg-sub ::query
  :<- [::gas/group-id]
  :<- [::selected-types]
  :<- [::reporting-common/selected-states]
  :<- [::expiration-date-range]
  :<- [::closing-date-range]
  :<- [::reporting-common/date-range]
  (fn [[group-id bond-types bond-states expiration-range closing-range [start-date end-date]] _]
    (cond-> {:report-type :reporting/bonds
             :group-id    group-id
             :start-date  start-date
             :end-date    end-date}
      (seq bond-types)
      (assoc :bond-types (seq bond-types))
      (seq bond-states)
      (assoc :bond-states (seq bond-states))
      (some? expiration-range)
      (assoc :bond-expiration-start (first expiration-range)
             :bond-expiration-end (second expiration-range))
      (some? closing-range)
      (assoc :bond-closing-start (first closing-range)
             :bond-closing-end (second closing-range)))))

(rf/reg-event-fx ::load-bond-templates.success
  (fn [{:keys [db]} [_ status resp]]
    (let [templates (:entities resp)]
      (cond-> {:db (assoc db ::bond-templates templates)}
        (map? templates) (assoc :fx [[:dispatch [::entities/merge-entity-db status templates]]])))))

(rf/reg-event-fx ::load-bond-templates.error
  (fn [{:keys [db]} [_ _ {:keys [error]}]]
    {:db       (dissoc db ::bond-templates)
     :dispatch [:notifications/add :toast :error :error/error error]}))

(rf/reg-event-fx ::load-bond-templates
  (fn [_ [_ domain-group-id]]
    {:comm/command {:command    :bond-template/get-all
                    :method     :get
                    :data       {:group-id domain-group-id}
                    :on-success [::load-bond-templates.success]
                    :on-error   [::load-bond-templates.error]}}))

(defn type-selector [{:keys [tr types header-key]}]
  (let [selected-types  (<sub [::selected-types])
        on-select-all   #(>evt [::set-selected-types (set (mapv first types))])
        on-deselect-all #(>evt [::set-selected-types #{}])]
    [:div
     [reporting-common/selector-header tr header-key on-select-all on-deselect-all]
     [:div.flex.flex-row.flex-wrap
      (for-frag [[id type] types]
        [:div.w-56
         (cc/checkbox {:id         (str id "-checkbox")
                       :label-text type
                       :checked    (contains? selected-types id)
                       :on-change  #(>evt [::set-selected-types
                                           ((if % conj disj) selected-types id)])})])]]))

(defn expiration-date-range-selector [tr id]
  (let [selected (<sub [::expiration-date-selector-mode])]
    [:div
     [:div.flex.flex-wrap.items-center
      [ui-select/select
       {:id           (str id "-select")
        :label-text   (tr [:reporting.bonds/expiration-time-range])
        :required     false
        :allow-empty? true
        :val->str     #(tr [%])
        :value        selected
        :tr           tr
        :on-change    (fn [value]
                        (>evt [::set-expiration-date-selector-mode value]))
        :values       expiration-date-range-options}]
      (when (= selected :reporting.bonds.expiration-options/custom)
        [:div.mt-6.flex.flex-wrap.sm:flex-nowrap
         [reporting-common/date-input tr ::expiration-custom-start-date
          {:label-loc  :reporting.bonds/expiration-start-date
           :class-name "mr-4 mb-2"
           :max-date   (date-util/add-months-to-date (js/Date.) 24)
           :callback   #(>evt [::select-expiration-date ::expiration-custom-start-date (date-util/start-of-day %)])}]
         [reporting-common/date-input tr ::expiration-custom-end-date
          {:label-loc  :reporting.bonds/expiration-end-date
           :class-name "mb-2"
           :callback   #(>evt [::select-expiration-date ::expiration-custom-end-date (date-util/end-of-day %)])
           :max-date   (date-util/add-months-to-date (js/Date.) 24)
           :min-date   (<sub [::expiration-custom-start-date])}]])]]))

(defn closing-date-range-selector [tr id]
  (let [selected (<sub [::closing-date-selector-mode])]
    [:div
     [:div.flex.flex-wrap.items-center
      [ui-select/select
       {:id           (str id "-select")
        :label-text   (tr [:reporting.bonds/closing-time-range])
        :required     false
        :allow-empty? true
        :val->str     #(tr [%])
        :value        selected
        :tr           tr
        :on-change    (fn [value]
                        (>evt [::set-closing-date-selector-mode value]))
        :values       closing-date-range-options}]
      (when (= selected :reporting.bonds.closing-options/custom)
        [:div.mt-6.flex.flex-wrap.sm:flex-nowrap
         [reporting-common/date-input tr ::closing-custom-start-date
          {:label-loc  :reporting.bonds/closing-start-date
           :class-name "mr-4 mb-2"
           :max-date   (js/Date.)
           :callback   #(>evt [::select-closing-date ::closing-custom-start-date (date-util/start-of-day %)])}]
         [reporting-common/date-input tr ::closing-custom-end-date
          {:label-loc  :reporting.bonds/closing-end-date
           :class-name "mb-2"
           :callback   #(>evt [::select-closing-date ::closing-custom-end-date (date-util/end-of-day %)])
           :max-date   (js/Date.)
           :min-date   (<sub [::closing-custom-start-date])}]])]]))

(defn view [match]
  (let [tr         (<sub [::gas/group-tr])
        query      (<sub [::query])
        lang       (<sub [:language])
        bond-types (<sub [::bond-type-options lang])]
    [reporting-common/report-page
     match
     tr
     [:section
      [type-selector {:tr tr :types bond-types :header-key :reporting.bonds/type}]
      [reporting-common/status-selector {:tr tr :states bond-states :header-key :reporting.bonds/status}]
      [reporting-common/date-range-selector tr "bonds-date-range" reporting-common/date-range-options]
      [expiration-date-range-selector tr "bonds-expiration-range"]
      [closing-date-range-selector tr "bonds-closing-range"]]
     [rdb/generate-button (util/mmap query tr)]
     [report-list/list-section (util/mmap {:report-type :reporting/bonds} tr)]]))

(def route
  {:name        :view/group.reporting.bonds
   :view        view
   :parameters  {:path {:group-id s/Int}}
   :controllers [{:parameters {:path [:group-id]}
                  :start      (fn [params]
                                (let [group-id (-> params :path :group-id)]
                                  (>evt [::reporting-filters/fetch-saved-filters group-id])
                                  (>evt [::reporting-common/init group-id (first reporting-common/date-range-options)])
                                  (>evt [::load-bond-templates group-id])))}]})
